import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { motion } from 'framer-motion';
import './ProgressAnalytics.css';

const ProgressAnalytics = ({ pathData, progressHistory = [] }) => {
  // Sample velocity data (hours per week over time)
  const velocityData = progressHistory.map((entry, index) => ({
    week: `W${index + 1}`,
    hours: entry.hoursThisWeek || 0,
    target: 12,
  }));

  // Sample skills mastered over time
  const skillsData = progressHistory.map((entry, index) => ({
    week: `W${index + 1}`,
    skills: entry.skillsCompleted || 0,
  }));

  // Calculate current streak
  const calculateStreak = () => {
    if (!progressHistory.length) return 0;
    let streak = 0;
    const today = new Date();
    
    for (let i = progressHistory.length - 1; i >= 0; i--) {
      const entryDate = new Date(progressHistory[i].date);
      const daysDiff = Math.floor((today - entryDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === streak) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const streak = calculateStreak();
  const totalHours = progressHistory.reduce((sum, entry) => sum + (entry.hoursThisWeek || 0), 0);
  const avgHoursPerWeek = progressHistory.length > 0 ? (totalHours / progressHistory.length).toFixed(1) : 0;
  const totalSkills = progressHistory.reduce((sum, entry) => sum + (entry.skillsCompleted || 0), 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="progress-analytics-container">
      <div className="analytics-header">
        <h3>📊 Your Learning Analytics</h3>
        <p>Track your progress and stay motivated</p>
      </div>

      {/* Key Metrics */}
      <div className="key-metrics">
        <motion.div
          className="metric-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="metric-icon streak">🔥</div>
          <div className="metric-content">
            <div className="metric-value">{streak}</div>
            <div className="metric-label">Day Streak</div>
          </div>
        </motion.div>

        <motion.div
          className="metric-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="metric-icon hours">⏱️</div>
          <div className="metric-content">
            <div className="metric-value">{totalHours}</div>
            <div className="metric-label">Total Hours</div>
          </div>
        </motion.div>

        <motion.div
          className="metric-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="metric-icon skills">🎯</div>
          <div className="metric-content">
            <div className="metric-value">{totalSkills}</div>
            <div className="metric-label">Skills Mastered</div>
          </div>
        </motion.div>

        <motion.div
          className="metric-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="metric-icon avg">📈</div>
          <div className="metric-content">
            <div className="metric-value">{avgHoursPerWeek}h</div>
            <div className="metric-label">Avg/Week</div>
          </div>
        </motion.div>
      </div>

      {/* Learning Velocity Chart */}
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h4>⚡ Learning Velocity</h4>
        <p className="chart-description">Hours spent learning each week</p>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={velocityData}>
            <defs>
              <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="week" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="hours" 
              stroke="#8b5cf6" 
              fillOpacity={1} 
              fill="url(#colorHours)" 
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="target" 
              stroke="#ef4444" 
              strokeDasharray="5 5" 
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Skills Progress Chart */}
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h4>🎓 Skills Mastered Over Time</h4>
        <p className="chart-description">Cumulative skills completed</p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={skillsData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="week" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="skills" fill="#3b82f6" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Streak Calendar */}
      <motion.div
        className="streak-calendar"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h4>🔥 Activity Streak</h4>
        <p className="chart-description">Your daily learning activity</p>
        <div className="calendar-grid">
          {Array.from({ length: 28 }, (_, i) => {
            const hasActivity = i < progressHistory.length && progressHistory[i]?.hoursThisWeek > 0;
            return (
              <motion.div
                key={i}
                className={`calendar-day ${hasActivity ? 'active' : ''}`}
                whileHover={{ scale: 1.1 }}
                title={hasActivity ? `${progressHistory[i]?.hoursThisWeek}h` : 'No activity'}
              />
            );
          })}
        </div>
        <div className="calendar-legend">
          <span>Less</span>
          <div className="legend-dots">
            <div className="legend-dot level-0"></div>
            <div className="legend-dot level-1"></div>
            <div className="legend-dot level-2"></div>
            <div className="legend-dot level-3"></div>
          </div>
          <span>More</span>
        </div>
      </motion.div>

      {/* Insights */}
      <motion.div
        className="insights-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h4>💡 Insights</h4>
        <div className="insights-grid">
          {avgHoursPerWeek >= 12 && (
            <div className="insight insight-success">
              <div className="insight-icon">🎉</div>
              <div className="insight-text">
                <strong>Great pace!</strong> You're averaging {avgHoursPerWeek}h/week
              </div>
            </div>
          )}
          {avgHoursPerWeek < 12 && avgHoursPerWeek > 0 && (
            <div className="insight insight-warning">
              <div className="insight-icon">⚠️</div>
              <div className="insight-text">
                <strong>Increase effort:</strong> Try to reach 12h/week target
              </div>
            </div>
          )}
          {streak >= 7 && (
            <div className="insight insight-success">
              <div className="insight-icon">🔥</div>
              <div className="insight-text">
                <strong>{streak}-day streak!</strong> You're on fire!
              </div>
            </div>
          )}
          {totalSkills >= 3 && (
            <div className="insight insight-success">
              <div className="insight-icon">🎯</div>
              <div className="insight-text">
                <strong>Skill builder:</strong> {totalSkills} skills mastered
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ProgressAnalytics;
